import { Component, Input, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { AgMediaUploaderModalComponent } from '../../../ag-media-uploader/ag-media-uploader-modal.component';
import { BehaviorSubject, firstValueFrom, from, map, Subscription, switchMap } from 'rxjs';
import { AgencyMediaEditorService } from './agency-media-editor.service';
import { MediaAction } from '../../../ag-media-gallery/ag-media-gallery.models';
import { AgencyFileDirectoriesService } from '../../../../services/agency-file-directories.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { confirm } from 'devextreme/ui/dialog';
import { ToastrService } from 'ngx-toastr';
import { AgencyFilesMediaService } from '../../../../services/agency-files-media.service';
import { AgencyImagesService } from '../../../../services/agency-images.service';
import { ItemClickEvent } from 'devextreme/ui/menu';
import { MediaUploaderTabs } from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.models';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import {
  AgencyFileDirectory,
  EntityPermissionActivityKeys,
  AgencyFileDirectoryKeys,
  AGMedia,
  Entity,
  EntityPermissionModelKeys,
} from '@ag-common-lib/public-api';
import { FileDirectoryKeys, SystemFileDirectories } from '@ag-common-lib/lib/models/utils/file-directory.model';

@UntilDestroy()
@Component({
  selector: 'ag-shr-agency-media',
  templateUrl: './agency-media.component.html',
  styleUrls: ['./agency-media.component.scss'],
})
export class AgencyMediaComponent implements OnDestroy {
  @ViewChild(AgMediaUploaderModalComponent, { static: false }) mediaUploaderComponent: AgMediaUploaderModalComponent;

  @Input() set agencyDbId(agencyId: string) {
    this.agencyMediaEditorService.setAgencyId(agencyId);
    this.agencyMediaEditorService.setCurrentDirectory(null);
    this.mediaPathPrefix$.next(`agencies/${agencyId}`);
    this._agencyDbId = agencyId;
    this.selectedItemIndex = null;
    this.directoriesDataSource = [];
    this.directoriesLoaded = false;
    this.refreshDirectories();
  }

  mediaPathPrefix$ = new BehaviorSubject<string>('');
  directoriesDataSource: AgencyFileDirectory[];
  selectedItemIndex: number | null = 0;
  extraMediaActions: MediaAction[] = [];
  folderName = '';
  directoriesLoaded = false;
  protected EntityPermissionActivityKeys = EntityPermissionActivityKeys;

  private _agencyDbId: string;
  private directoriesSubscription: Subscription | null = null;
  protected readonly BaseModelKeys = BaseModelKeys;
  protected readonly AgencyFileDirectoryKeys = AgencyFileDirectoryKeys;
  protected readonly MediaUploaderTabs = MediaUploaderTabs;

  constructor(
    protected agencyMediaEditorService: AgencyMediaEditorService,
    private agencyFileDirectoriesService: AgencyFileDirectoriesService,
    private toastr: ToastrService,
    private agencyFilesMediaService: AgencyFilesMediaService,
    private agencyImagesService: AgencyImagesService,
  ) {}

  ngOnDestroy(): void {
    this.directoriesSubscription?.unsubscribe();
  }

  addFolder(): void {
    if (this.folderName) {
      const isCurrentFolderNameExist =
        this.directoriesDataSource?.filter(directory => directory.name == this.folderName).length > 0;
      if (isCurrentFolderNameExist) {
        this.toastr.error(`Folder Name "${this.folderName}" already exists`);
        return;
      }

      const directory: AgencyFileDirectory = {
        [AgencyFileDirectoryKeys.agencyDbId]: this._agencyDbId,
        [FileDirectoryKeys.name]: this.folderName,
        [FileDirectoryKeys.isSystem]: false,
      };

      this.agencyMediaEditorService.createFolder(directory).then(() => {
        this.folderName = '';
        this.refreshDirectories();
      });
    }
  }

  onRemoveFolder(clickEvent: { event?: Event }, folder: AgencyFileDirectory): void {
    clickEvent?.event?.stopPropagation();
    void this.deleteFolder(folder);
  }

  async deleteFolder(folder: AgencyFileDirectory): Promise<void> {
    const folderName = folder[FileDirectoryKeys.name] ?? '';
    const agencyId = folder[AgencyFileDirectoryKeys.agencyDbId];
    const directoryId = folder[BaseModelKeys.dbId];
    if (!agencyId || !directoryId) {
      return;
    }

    const items =
      (await firstValueFrom(this.agencyFilesMediaService.getList(agencyId, directoryId, [], { ignoreCash: true }))) ??
      [];
    if (items.length) {
      this.toastr.error(`Folder "${folderName}" contains media. Please remove media before.`);
      return;
    }

    const isConfirmed = await confirm(
      `<i>Are you sure you want to Delete Folder "${folderName}"?</i>`,
      'Confirm',
    );
    if (!isConfirmed) {
      return;
    }

    await this.agencyMediaEditorService.deleteFolder(agencyId, directoryId);
    this.refreshDirectories();
  }

  uploadFile(): void {
    this.mediaUploaderComponent.showModal();
  }

  onMediaChanged(media: AGMedia): void {
    this.agencyMediaEditorService.createMedia(this._agencyDbId, media);
  }

  menuItemClick({ itemData }: ItemClickEvent<any>): void {
    this.agencyMediaEditorService.setCurrentDirectory(itemData as any);
  }

  onDeleteClicked = (media: AGMedia): Promise<boolean> => {
    return this.agencyMediaEditorService.deleteMedia(this._agencyDbId, media);
  };

  private refreshDirectories(): void {
    const agencyId = this._agencyDbId;
    this.directoriesSubscription?.unsubscribe();

    if (!agencyId) {
      this.directoriesDataSource = [];
      this.directoriesLoaded = true;
      return;
    }

    this.directoriesSubscription = from(this.agencyImagesService.ensureImagesDirectory(agencyId))
      .pipe(
        switchMap(() => this.agencyFileDirectoriesService.getList(agencyId)),
        map(fileDirectories => {
          if (this._agencyDbId !== agencyId) {
            return;
          }

          this.applyDirectories(fileDirectories ?? []);
        }),
      )
      .subscribe({
        error: () => {
          if (this._agencyDbId !== agencyId) {
            return;
          }

          this.directoriesLoaded = true;
        },
      });
  }

  private applyDirectories(fileDirectories: AgencyFileDirectory[]): void {
    this.directoriesDataSource = fileDirectories;
    this.directoriesLoaded = true;

    if (!fileDirectories.length) {
      this.agencyMediaEditorService.setCurrentDirectory(null);
      this.selectedItemIndex = null;
      return;
    }

    const currentDirectoryId = this.agencyMediaEditorService.currentDirectory?.[BaseModelKeys.dbId];
    const currentIndex = fileDirectories.findIndex(directory => directory[BaseModelKeys.dbId] === currentDirectoryId);
    if (currentIndex >= 0) {
      this.selectedItemIndex = currentIndex;
      return;
    }

    this.setDefaultDirectory(fileDirectories);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private setDefaultDirectory(fileDirectories: AgencyFileDirectory[]): void {
    const imagesIndex = fileDirectories.findIndex(
      directory => directory[FileDirectoryKeys.name] === SystemFileDirectories.Images,
    );
    const defaultIndex = imagesIndex >= 0 ? imagesIndex : 0;
    this.agencyMediaEditorService.setCurrentDirectory(fileDirectories[defaultIndex]);
    this.selectedItemIndex = defaultIndex;
  }

  protected readonly Entity = Entity;
  protected readonly EntityPermissionModelKeys = EntityPermissionModelKeys;
}
